import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Header from 'shared/components/Header';
import PageLoader from 'shared/components/PageLoader';
import { useCourierAuth } from 'features/courier-auth/context/CourierAuthContext';
import {
  type CourierSubmissionView,
  fetchSubmission,
} from 'features/courier-portal/services/courierPortalService';
import RecruitmentStageSection from 'features/courier-portal/components/RecruitmentStageSection';
import CourierDetailsDrawer from 'features/courier-portal/components/CourierDetailsDrawer';
import WelcomeHero from 'features/courier-portal/components/WelcomeHero';
import RecruitmentKPIs from 'features/courier-portal/components/RecruitmentKPIs';
import PortalFeatureCards from 'features/courier-portal/components/PortalFeatureCards';
import { useRecruitmentStatus } from 'features/courier-portal/hooks/useRecruitmentStatus';
import { useTrainingStatus } from 'features/courier-portal/hooks/useTrainingStatus';

const CourierPortalPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { courier, logout } = useCourierAuth();
  const [submission, setSubmission] = useState<CourierSubmissionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    status,
    loading: statusLoading,
    errored: statusErrored,
    refresh: refreshStatus,
  } = useRecruitmentStatus();
  const {
    training,
    loading: trainingLoading,
    refresh: refreshTraining,
  } = useTrainingStatus();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSubmission()
      .then((s) => {
        if (!cancelled) setSubmission(s);
      })
      .catch(() => {
        if (!cancelled) setSubmission(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const logoutLabel = String(t('portal.logout'));

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <Header onLogout={handleLogout} logoutLabel={logoutLabel} />
        <PageLoader />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <Header onLogout={handleLogout} logoutLabel={logoutLabel} />
        <main className="flex-1 flex items-center justify-center px-4">
          <p className="text-sm text-gray-600">{t('portal.updateFailed')}</p>
        </main>
      </div>
    );
  }

  const displayName = courier?.fullName ?? submission.fullName;

  const handleStatusRefresh = () => {
    void Promise.all([refreshStatus(), refreshTraining()]);
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header onLogout={handleLogout} logoutLabel={logoutLabel} />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <WelcomeHero
          name={displayName}
          onViewDetails={() => setDrawerOpen(true)}
        />

        <RecruitmentKPIs status={status} loading={statusLoading} />

        <RecruitmentStageSection
          status={status}
          loading={statusLoading}
          errored={statusErrored}
          onRefresh={handleStatusRefresh}
        />

        <PortalFeatureCards
          trainingComplete={training?.isComplete ?? false}
          loading={trainingLoading}
        />
      </main>

      <CourierDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        submission={submission}
      />
    </div>
  );
};

export default CourierPortalPage;
